import React, { useState, useEffect, useContext } from 'react';
import {
  Table, Badge, Button, Dropdown,
  Row, Col, Modal, Form, Alert, InputGroup,
} from 'react-bootstrap';
import axios from 'axios';
import AppContext from '../../../context/AppContext';

const fmt = (n) => `PKR ${(n || 0).toLocaleString()}`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
const initials = (s = '') =>
  s.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const BOOKING_STATUS = { confirmed: 'success', pending: 'warning', cancelled: 'danger', completed: 'info' };
const PAYMENT_STATUS = { paid: 'success', unpaid: 'danger', refunded: 'info', partial_refund: 'warning', pending: 'warning' };

const SBadge = ({ status, map }) => {
  const bg = (map || BOOKING_STATUS)[status?.toLowerCase()] || 'secondary';
  return (
    <Badge bg={bg} className="adm-bkd-badge">
      {status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
    </Badge>
  );
};

const Timeline = ({ status }) => {
  const steps =
    status === 'cancelled'
      ? ['pending', 'confirmed', 'cancelled']
      : ['pending', 'confirmed', 'completed'];
  const idx = steps.indexOf(status?.toLowerCase());
  return (
    <div className="adm-bkd-timeline">
      {steps.map((step, i) => (
        <div key={step} className={`adm-bkd-tl-item ${i < idx ? 'done' : i === idx ? 'active' : ''}`}>
          {i < steps.length - 1 && <div className="adm-bkd-tl-line" />}
          <div className="adm-bkd-tl-dot">
            {i < idx ? <i className="bi bi-check-lg" /> : i + 1}
          </div>
          <div className="adm-bkd-tl-label">{step.charAt(0).toUpperCase() + step.slice(1)}</div>
        </div>
      ))}
    </div>
  );
};

const Chunk = ({ icon, title, children, variant }) => (
  <div className="adm-bkd-chunk">
    <div className={`adm-bkd-chunk-header ${variant ? `adm-bkd-chunk-header-${variant}` : ''}`}>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </div>
    <div className="adm-bkd-chunk-body">{children}</div>
  </div>
);

const Field = ({ label, mono, children }) => (
  <div className="adm-bkd-field">
    <label className="adm-bkd-field-label">{label}</label>
    <div className={`adm-bkd-field-value${mono ? ' adm-bkd-mono' : ''}`}>{children || 'N/A'}</div>
  </div>
);

const Divider = () => <div className="adm-bkd-divider" />;

const Sub = ({ icon, children }) => (
  <p className="adm-bkd-sub-title">
    {icon && <i className={`bi ${icon}`} />}
    {children}
  </p>
);

const arrBadge = (raw) =>
  (Array.isArray(raw) ? raw : String(raw || '').split(','))
    .filter(Boolean)
    .map((v, i) => (
      <Badge key={i} bg="secondary" className="adm-bkd-badge me-1 mb-1">
        {v.toString().trim()}
      </Badge>
    ));

const RefundModal = ({ show, onHide, booking, onProcess }) => {
  const [type, setType] = useState('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!booking) return null;
  const max = booking.jjpayment?.total_amount || booking.total_amount || 0;

  const handleType = (t) => {
    setType(t);
    setAmount(t === 'full' ? String(max) : '');
    setError('');
  };

  const handleSubmit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return; }
    if (amt > max) { setError(`Max refundable: ${fmt(max)}`); return; }
    if (!reason.trim()) { setError('Reason is required'); return; }
    setBusy(true);
    onProcess(booking.id, amt, reason)
      .then(() => {
        setBusy(false);
        setType('full'); setAmount(''); setReason(''); setError('');
        onHide();
      })
      .catch(() => { setBusy(false); setError('Failed to process refund. Try again.'); });
  };

  return (
    <Modal show={show} onHide={onHide} centered className="adm-bkd-modal">
      <Modal.Header closeButton className="adm-bkd-modal-hd adm-bkd-modal-hd-danger">
        <Modal.Title><i className="bi bi-arrow-counterclockwise" /> Process Refund</Modal.Title>
      </Modal.Header>
      <Modal.Body className="adm-bkd-modal-body">
        {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
        <div className="adm-bkd-modal-summary mb-3">
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Booking ID</span>
            <strong className="adm-bkd-mono">{booking.id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Customer</span>
            <strong>{booking.jjcustomer?.cus_username}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Max Refund</span>
            <strong className="text-success">{fmt(max)}</strong>
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Refund Type</Form.Label>
          <div className="adm-bkd-radio-group">
            <Form.Check type="radio" id="rf-full" label={`Full Refund (${fmt(max)})`} checked={type === 'full'} onChange={() => handleType('full')} />
            <Form.Check type="radio" id="rf-partial" label="Partial Refund" checked={type === 'partial'} onChange={() => handleType('partial')} />
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Refund Amount <span className="text-danger">*</span></Form.Label>
          <InputGroup>
            <InputGroup.Text className="adm-bkd-filter-icon">PKR</InputGroup.Text>
            <Form.Control type="number" className="adm-bkd-filter-input" value={amount} onChange={e => { setAmount(e.target.value); setError(''); }} disabled={type === 'full'} min="0" max={max} />
          </InputGroup>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Reason <span className="text-danger">*</span></Form.Label>
          <Form.Control as="textarea" rows={3} className="adm-bkd-filter-input" placeholder="Reason for refund…" value={reason} onChange={e => setReason(e.target.value)} />
        </Form.Group>
        <Alert variant="warning" className="py-2 mb-0">
          <i className="bi bi-exclamation-triangle me-2" />This action cannot be undone. Customer will be notified.
        </Alert>
      </Modal.Body>
      <Modal.Footer className="adm-bkd-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={handleSubmit} disabled={busy || !amount || !reason}>
          {busy ? <><span className="spinner-border spinner-border-sm me-2" />Processing…</> : <><i className="bi bi-check-circle me-2" />Process Refund</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SettlementModal = ({ show, onHide, payment, onApprove }) => {
  const [method, setMethod] = useState('bank_transfer');
  const [txnId, setTxnId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!payment) return null;

  const handleApprove = () => {
    if (!txnId.trim()) { setError('Transaction / Reference ID is required'); return; }
    setBusy(true);
    onApprove(payment.id, method, txnId, notes)
      .then(() => {
        setBusy(false);
        setMethod('bank_transfer'); setTxnId(''); setNotes(''); setError('');
        onHide();
      })
      .catch(() => { setBusy(false); setError('Failed to approve settlement. Try again.'); });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="adm-bkd-modal">
      <Modal.Header closeButton className="adm-bkd-modal-hd adm-bkd-modal-hd-success">
        <Modal.Title><i className="bi bi-check-circle" /> Approve Vendor Settlement</Modal.Title>
      </Modal.Header>
      <Modal.Body className="adm-bkd-modal-body">
        {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
        <div className="adm-bkd-modal-summary mb-3">
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Payment ID</span>
            <strong className="adm-bkd-mono">{payment.id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Booking</span>
            <strong className="adm-bkd-mono">{payment.jjbooking_id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Vendor Payout</span>
            <strong className="text-success">{fmt(payment.vendor_amount)}</strong>
          </div>
        </div>
        <div className="adm-bkd-breakdown mb-3">
          <div className="adm-bkd-breakdown-row">
            <span>Booking Total</span><strong>{fmt(payment.total_amount)}</strong>
          </div>
          <div className="adm-bkd-breakdown-row">
            <span>Platform Commission (15%)</span>
            <strong className="text-danger">— {fmt(payment.admin_commission)}</strong>
          </div>
          <div className="adm-bkd-breakdown-row adm-bkd-breakdown-total">
            <span>Vendor Payout</span>
            <strong className="text-success">{fmt(payment.vendor_amount)}</strong>
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Payment Method <span className="text-danger">*</span></Form.Label>
          <Form.Select className="adm-bkd-filter-select" value={method} onChange={e => setMethod(e.target.value)}>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="jazzcash">JazzCash</option>
            <option value="easypaisa">EasyPaisa</option>
            <option value="cheque">Cheque</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Transaction / Reference ID <span className="text-danger">*</span></Form.Label>
          <Form.Control type="text" className="adm-bkd-filter-input" placeholder="Bank TXN ID, wallet ref, or cheque number" value={txnId} onChange={e => { setTxnId(e.target.value); setError(''); }} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Additional Notes (Optional)</Form.Label>
          <Form.Control as="textarea" rows={2} className="adm-bkd-filter-input" placeholder="Any notes…" value={notes} onChange={e => setNotes(e.target.value)} />
        </Form.Group>
        <Alert variant="info" className="py-2 mb-2">
          <i className="bi bi-info-circle me-2" />Confirm that <strong>{fmt(payment.vendor_amount)}</strong> has been transferred via <strong>{method.replace('_', ' ').toUpperCase()}</strong>.
        </Alert>
        <Alert variant="warning" className="py-2 mb-0">
          <i className="bi bi-exclamation-triangle me-2" />This action cannot be reversed once approved.
        </Alert>
      </Modal.Body>
      <Modal.Footer className="adm-bkd-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="success" onClick={handleApprove} disabled={busy || !txnId.trim()}>
          {busy ? <><span className="spinner-border spinner-border-sm me-2" />Processing…</> : <><i className="bi bi-check-circle-fill me-2" />Approve Settlement</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const BookingDetailPanel = ({ booking, onBack, onRefresh }) => {
  const [localBk, setLocalBk] = useState(booking);
  const [showRefund, setShowRefund] = useState(false);
  const [showSettlement, setShowSettlement] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const b = localBk;
  const customer = b?.jjcustomer || {};
  const vendor = b?.jjvendor || {};
  const hotel = vendor?.jjhotelregistration || {};
  const rooms = hotel?.jjrooms || [];
  const paymentMethods = vendor?.jjpaymentmethods || [];
  const p = b?.jjpayment || null;

  // ── mock refund (replace with real axios.post) ──
  const handleRefund = (bkId, amt, rsn) =>
    new Promise((res) => {
      setTimeout(() => {
        setLocalBk((prev) => ({
          ...prev,
          payment_status: 'refunded',
          refund_amount: amt,
          refund_date: new Date().toISOString(),
          refund_reason: rsn,
        }));
        setSuccessMsg('Refund processed successfully!');
        res();
      }, 700);
    });

  // ── mock settlement approval (replace with real axios.post) ──
  const handleSettlement = (payId, method, txnId, notes) =>
    new Promise((res) => {
      setTimeout(() => {
        setLocalBk((prev) => ({
          ...prev,
          jjpayment: { ...prev.jjpayment, status: 'paid', payment_method: method, stripe_payment_intent_id: txnId },
        }));
        setSuccessMsg('Settlement approved successfully!');
        res();
      }, 700);
    });

  return (
    <div className="adm-bkd-detail-panel">

      {/* ── Header ── */}
      <div className="adm-bkd-page-header mb-3">
        <div>
          <h2 className="adm-bkd-page-title">
            Booking — <span className="adm-bkd-mono" style={{ fontSize: '1.1rem' }}>{b.id}</span>
          </h2>
          <p className="adm-bkd-page-subtitle" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <SBadge status={b.booking_status} map={BOOKING_STATUS} />
            <SBadge status={b.payment_status} map={PAYMENT_STATUS} />
            <span style={{ color: 'var(--adm-bkd-text-light)', fontSize: '.78rem' }}>
              Created: {fmtDate(b.created_at)}
            </span>
          </p>
        </div>
        <Button className="adm-bkd-back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left me-2" />Back to Bookings
        </Button>
      </div>

      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg('')} className="mb-3">
          <i className="bi bi-check-circle me-2" />{successMsg}
        </Alert>
      )}

      {/* Single-column stacked layout: each section in its own row */}
      <div className="adm-bkd-detail-stack">

        {/* ── 1. Customer Information ── */}
        <Chunk icon="bi-person-circle" title="1. Customer Information">
          <div className="adm-bkd-customer-header mb-3">
            <div className="adm-bkd-avatar">{initials(customer.cus_username)}</div>
            <div>
              <div className="adm-bkd-customer-name">{customer.cus_username || 'N/A'}</div>
              <div className="adm-bkd-customer-role">
                <Badge bg="secondary" className="adm-bkd-badge">{customer.cus_role || 'Customer'}</Badge>
                <Badge bg={customer.cus_status === 'active' ? 'success' : 'danger'} className="adm-bkd-badge">
                  {customer.cus_status?.toUpperCase() || 'N/A'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="adm-bkd-field-grid">
            <Field label="Email">{customer.cus_email}</Field>
            <Field label="Phone">{b.phone_no}</Field>
            <Field label="City">{b.cus_city}</Field>
            <Field label="Guests">{b.number_of_guests} Persons</Field>
            <Field label="Customer ID" mono>{customer.id}</Field>
          </div>
        </Chunk>

        {/* ── 2. Hotel Information ── */}
        <Chunk icon="bi-building" title="2. Hotel Information">
          <div className="adm-bkd-hotel-card mb-3">
            <div className="adm-bkd-hotel-name">{hotel.hotel_name || 'N/A'}</div>
            <div className="adm-bkd-hotel-meta">
              {hotel.stars && (
                <span className="adm-bkd-stars">
                  {'★'.repeat(hotel.stars)}{'☆'.repeat(5 - hotel.stars)}
                  <span style={{ color: 'var(--adm-bkd-text-light)', marginLeft: 6, fontFamily: 'inherit', fontWeight: 500 }}>
                    {hotel.stars}-Star
                  </span>
                </span>
              )}
              <span><i className="bi bi-geo-alt me-1" />{hotel.address || 'N/A'}</span>
              <span><i className="bi bi-telephone me-1" />{hotel.phone || 'N/A'}</span>
              <span><i className="bi bi-person me-1" />Owner: {hotel.owner_name || 'N/A'}</span>
            </div>
            <div className="adm-bkd-hotel-times">
              <span><i className="bi bi-box-arrow-in-right me-1" />Check-in: {hotel.check_in_time || 'N/A'}</span>
              <span><i className="bi bi-box-arrow-left me-1" />Check-out: {hotel.check_out_time || 'N/A'}</span>
            </div>
          </div>
          {hotel.facilities && (
            <>
              <Sub icon="bi-star">Facilities</Sub>
              <div className="adm-bkd-amenities">{arrBadge(hotel.facilities)}</div>
            </>
          )}
          {hotel.description && (
            <>
              <Divider />
              <Sub icon="bi-card-text">Description</Sub>
              <p style={{ fontSize: '.82rem', color: 'var(--adm-bkd-text-mid)', margin: 0 }}>{hotel.description}</p>
            </>
          )}
          {hotel.policies_url && (
            <>
              <Divider />
              <div className="adm-bkd-policy-box">
                <a href={hotel.policies_url} target="_blank" rel="noreferrer">
                  <i className="bi bi-shield-check me-1" />View Policy Document <i className="bi bi-box-arrow-up-right ms-1" />
                </a>
              </div>
            </>
          )}
        </Chunk>

       {/* ── 3. Room Details (Booked Room Only) ── */}
<Chunk icon="bi-door-open" title="3. Booked Room Details">
  {b.jjroom ? (
    <div className="adm-bkd-room-card">
      <div className="adm-bkd-room-header">
        <span className="adm-bkd-room-pill">{b.jjroom.room_type}</span>
        <Badge bg={b.jjroom.status?.toLowerCase() === 'available' ? 'success' : 'warning'} className="adm-bkd-badge">
          {b.jjroom.status?.toUpperCase() || 'N/A'}
        </Badge>
      </div>
      <div className="adm-bkd-field-grid">
        <Field label="Room Number" mono>{b.jjroom.room_number}</Field>
        <Field label="Floor">{b.jjroom.floor}</Field>
        <Field label="Capacity">{b.jjroom.capacity} Persons</Field>
        <Field label="Price / Night">{fmt(b.jjroom.price)}</Field>
      </div>
      {b.jjroom.amenities && (
        <>
          <Divider />
          <Sub icon="bi-grid">Amenities</Sub>
          <div className="adm-bkd-amenities">{arrBadge(b.jjroom.amenities)}</div>
        </>
      )}
      {/* Optional: Room Images */}
      {b.jjroom.jjroomimages?.length > 0 && (
        <>
          <Divider />
          <Sub icon="bi-images">Room Images</Sub>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {b.jjroom.jjroomimages.map(img => (
              <img key={img.id} src={img.image_path} alt="Room" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
            ))}
          </div>
        </>
      )}
    </div>
  ) : (
    <div className="adm-bkd-empty-state" style={{ padding: '2rem' }}>
      <i className="bi bi-door-closed" />
      <p>No room information available for this booking.</p>
    </div>
  )}
</Chunk>

        {/* ── 4. Booking Information ── */}
        <Chunk icon="bi-calendar-check" title="4. Booking Information">
          <div className="adm-bkd-bk-id-row mb-2">
            <Field label="Booking ID" mono>{b.id}</Field>
            <div className="adm-bkd-status-badges">
              <SBadge status={b.booking_status} map={BOOKING_STATUS} />
            </div>
          </div>
          <div className="adm-bkd-field-grid">
            <Field label="Booking Date">{fmtDate(b.created_at)}</Field>
            <Field label="Check-In">{fmtDate(b.checkIn || b.check_in)}</Field>
            <Field label="Check-Out">{fmtDate(b.checkOut || b.check_out)}</Field>
            <Field label="Guests">{b.number_of_guests} Persons</Field>
            <Field label="Booking Type">
              <Badge bg="info" className="adm-bkd-badge">{b.booking_type?.toUpperCase() || 'HOTEL'}</Badge>
            </Field>
            <Field label="Total Amount">
              <strong className="text-success">{fmt(b.total_amount)}</strong>
            </Field>
          </div>
          <Divider />
          <Sub icon="bi-bar-chart-steps">Status Timeline</Sub>
          <Timeline status={b.booking_status} />
        </Chunk>

        {/* ── 5. Vendor Information ── */}
        <Chunk icon="bi-building-check" title="5. Vendor Information">
          <Sub icon="bi-person-badge">Vendor Profile</Sub>
          <div className="adm-bkd-field-grid mb-3">
            <Field label="Vendor Name">{vendor.name}</Field>
            <Field label="Email">{vendor.email}</Field>
            <Field label="Role">{vendor.role?.toUpperCase() || 'VENDOR'}</Field>
            <Field label="Status">
              <Badge bg={vendor.verification_status === 'active' ? 'success' : 'warning'} className="adm-bkd-badge">
                {vendor.verification_status?.toUpperCase() || 'N/A'}
              </Badge>
            </Field>
            <Field label="Vendor ID" mono>{vendor.id}</Field>
          </div>

          <Divider />
          <Sub icon="bi-bank">Registered Payment Methods</Sub>
          {paymentMethods.length > 0 ? paymentMethods.map((pm, i) => (
            <div key={pm.id || i} className="adm-bkd-vendor-acct-card mb-2">
              <div className="adm-bkd-field-grid">
                <Field label="Name">{pm.name}</Field>
                <Field label="Type">{pm.type?.replace('_', ' ').toUpperCase()}</Field>
                <Field label="Account No." mono>{pm.account_number}</Field>
              </div>
            </div>
          )) : (
            <p style={{ fontSize: '.82rem', color: 'var(--adm-bkd-text-light)' }}>No bank account registered.</p>
          )}
        </Chunk>

        {/* ── 6. Booking Payment Information ── */}
        <Chunk icon="bi-credit-card" title="6. Booking Payment Information">
          {p ? (
            <>
              <div className="adm-bkd-pay-id-row mb-2">
                <Field label="Payment ID" mono>{p.id}</Field>
                <div className="adm-bkd-status-badges">
                  <SBadge status={b.payment_status} map={PAYMENT_STATUS} />
                </div>
              </div>
              <div className="adm-bkd-field-grid mb-3">
                <Field label="Transaction ID" mono>{p.stripe_payment_intent_id || '—'}</Field>
                <Field label="Method">{p.payment_method?.replace('_', ' ').toUpperCase() || '—'}</Field>
                <Field label="Paid At">{fmtDate(p.paid_at)}</Field>
                <Field label="Settlement Status">
                  <Badge bg={p.status === 'paid' ? 'success' : 'warning'} className="adm-bkd-badge">
                    {p.status === 'paid' ? 'COMPLETED' : 'AWAITING APPROVAL'}
                  </Badge>
                </Field>
              </div>

              <Divider />
              <Sub icon="bi-cash-stack">Payment Breakdown</Sub>
              <div className="adm-bkd-breakdown">
                <div className="adm-bkd-breakdown-row">
                  <span>Total Booking Amount</span>
                  <strong className="text-success">{fmt(p.total_amount || b.total_amount)}</strong>
                </div>
                <div className="adm-bkd-breakdown-row">
                  <span>Platform Commission (15%)</span>
                  <strong style={{ color: 'var(--adm-bkd-primary-mid)' }}>{fmt(p.admin_commission)}</strong>
                </div>
                <div className="adm-bkd-breakdown-row adm-bkd-breakdown-total">
                  <span>Vendor Payout</span>
                  <strong style={{ color: '#0c5460' }}>{fmt(p.vendor_amount)}</strong>
                </div>
              </div>

              {b.payment_status === 'refunded' && b.refund_amount && (
                <div className="adm-bkd-refund-box mt-3">
                  <Sub icon="bi-arrow-counterclockwise">Refund Information</Sub>
                  <div className="adm-bkd-field-grid">
                    <Field label="Refund Amount"><span className="text-danger fw-bold">{fmt(b.refund_amount)}</span></Field>
                    <Field label="Refund Date">{fmtDate(b.refund_date)}</Field>
                    {b.refund_reason && <Field label="Reason">{b.refund_reason}</Field>}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--adm-bkd-text-light)' }}>
              <i className="bi bi-credit-card d-block mb-2" style={{ fontSize: '1.8rem', opacity: .35 }} />
              <p style={{ fontSize: '.88rem' }}>No payment record found for this booking.</p>
            </div>
          )}
        </Chunk>

      </div>

      {/* ── Action Buttons ── */}
      <div className="adm-bkd-detail-actions">
        {b.payment_status === 'paid' && (
          <Button className="adm-bkd-btn-primary" onClick={() => setShowRefund(true)}>
            <i className="bi bi-arrow-counterclockwise me-2" />Process Refund
          </Button>
        )}
        {p && p.status !== 'paid' && (
          <Button variant="success" className="adm-bkd-approve-btn" onClick={() => setShowSettlement(true)}>
            <i className="bi bi-check-circle me-2" />Approve Settlement
          </Button>
        )}
        {customer.cus_email && (
          <Button variant="outline-secondary" className="adm-bkd-outline-btn" onClick={() => { window.location.href = `mailto:${customer.cus_email}`; }}>
            <i className="bi bi-envelope me-2" />Email Customer
          </Button>
        )}
        <Button variant="outline-secondary" className="adm-bkd-outline-btn">
          <i className="bi bi-download me-2" />Download Receipt
        </Button>
        <Button className="adm-bkd-back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left me-2" />Back to Bookings
        </Button>
      </div>

      <RefundModal show={showRefund} onHide={() => setShowRefund(false)} booking={b} onProcess={handleRefund} />
      <SettlementModal show={showSettlement} onHide={() => setShowSettlement(false)} payment={p} onApprove={handleSettlement} />
    </div>
  );
};

// ── Main Booking Table ────────────────────────────────────────────────
const AdmHotelBookingTable = ({ onViewPayment }) => {
  const { authToken, baseUrl, setSelectedBookingId } = useContext(AppContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);   // null = table, object = detail

  const fetchBookings = () => {
    setLoading(true);
    setError('');
    axios
      .post(`${baseUrl}/admin/view-booking`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const all = res.data?.data || [];
        // Only hotel bookings
        setBookings(all.filter((b) => b.booking_type === 'hotel'));
      })
      .catch(() => {
        setError('Failed to load bookings. Please try again.');
        setBookings([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleView = (bk) => {
    setSelected(bk);
    setSelectedBookingId?.(bk.id);
  };

  // ── Detail view ──
  if (selected) {
    return (
      <BookingDetailPanel
        booking={selected}
        onBack={() => { setSelected(null); fetchBookings(); }}
        onRefresh={fetchBookings}
      />
    );
  }

  // ── Loading ──
  if (loading) {
    return (
      <div className="adm-bkd-table-card">
        <div className="adm-bkd-detail-loading">
          <div className="spinner-border text-primary" role="status" />
          <p>Loading hotel bookings…</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="adm-bkd-table-card">
        <div className="adm-bkd-detail-loading">
          <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '2rem' }} />
          <p className="text-danger">{error}</p>
          <Button variant="outline-primary" size="sm" onClick={fetchBookings}>
            <i className="bi bi-arrow-clockwise me-1" />Retry
          </Button>
        </div>
      </div>
    );
  }

  // ── Table ──
  return (
    <div className="adm-bkd-table-card">
      <div className="adm-bkd-table-hd">
        <span className="adm-bkd-table-hd-title">
          <i className="bi bi-building" /> Hotel Booking Records
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="adm-bkd-table-count">{bookings.length} records</span>
          <Button variant="outline-secondary" size="sm" onClick={fetchBookings} style={{ fontSize: '.78rem', borderRadius: 6 }}>
            <i className="bi bi-arrow-clockwise" />
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <Table className="adm-bkd-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Booking ID</th>
              <th>Customer</th>
              <th>Hotel</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Amount</th>
              <th>Booking</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="10" className="adm-bkd-empty-state">
                  <i className="bi bi-inbox" />
                  <p>No hotel bookings found</p>
                </td>
              </tr>
            ) : bookings.map((bk, idx) => {
              const hotel = bk.jjvendor?.jjhotelregistration;
              return (
                <tr key={bk.id}>
                  <td style={{ color: 'var(--adm-bkd-text-light)', fontSize: '.78rem' }}>{idx + 1}</td>
                  <td>
                    <span className="adm-bkd-mono adm-bkd-id">{bk.id}</span>
                  </td>
                  <td>
                    <div className="fw-semibold">{bk.jjcustomer?.cus_username || 'N/A'}</div>
                    <small className="text-muted">{bk.jjcustomer?.cus_email || ''}</small>
                  </td>
                  <td>
                    <div className="fw-semibold">{hotel?.hotel_name || 'N/A'}</div>
                    {hotel?.stars && (
                      <small className="adm-bkd-stars" style={{ fontSize: '.7rem' }}>
                        {'★'.repeat(hotel.stars)}
                      </small>
                    )}
                  </td>
                  <td>{fmtDate(bk.checkIn || bk.check_in)}</td>
                  <td>{fmtDate(bk.checkOut || bk.check_out)}</td>
                  <td><strong className="text-success">{fmt(bk.total_amount)}</strong></td>
                  <td><SBadge status={bk.booking_status} map={BOOKING_STATUS} /></td>
                  <td><SBadge status={bk.payment_status} map={PAYMENT_STATUS} /></td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="adm-bkd-action-toggle">
                        <i className="bi bi-three-dots-vertical" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="adm-bkd-dropdown-menu">
                        <Dropdown.Item onClick={() => handleView(bk)}>
                          <i className="bi bi-eye me-2" />View Details
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => onViewPayment?.(bk.id)}>
                          <i className="bi bi-credit-card me-2" />View Payment
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item href={`mailto:${bk.jjcustomer?.cus_email}`}>
                          <i className="bi bi-envelope me-2" />Email Customer
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default AdmHotelBookingTable;